import { useSelector } from 'react-redux';
import { TaxRegionKey } from '@capintel/instruments-lib';
import { ReduxState } from '@/redux/modules/types';
import AppRegion from '@/types/app-region';
import { getUserAppRegion } from '@/utils/users';
import { useTaxAnalysisInputsUS } from './useTaxAnalysisInputsUS';
import { useTaxAnalysisInputsCA } from './useTaxAnalysisInputsCA';
import { PartialUsComparisonTaxSettings, PartialCaComparisonTaxSettings } from '@/types/tax-analysis';

export type UsTaxAnalysisInputValues = PartialUsComparisonTaxSettings;
export type CaTaxAnalysisInputValues = PartialCaComparisonTaxSettings;
export type TaxAnalysisInputValues = UsTaxAnalysisInputValues | CaTaxAnalysisInputValues;

/** Discriminated union for region-specific tax inputs */
export type RegionTaxInputs =
  | { region: typeof TaxRegionKey.US; values: UsTaxAnalysisInputValues }
  | { region: typeof TaxRegionKey.CA; values: CaTaxAnalysisInputValues }
  | undefined; // For unsupported regions or if user data is not loaded

/**
 * Main hook that determines user region and delegates to region-specific hooks.
 * Returns both region-specific setters and regionInputs for backward compatibility.
 */
export const useTaxAnalysisInputs = () => {
  const user = useSelector((state: ReduxState) => state.auth.user);
  const userRegion = getUserAppRegion(user);

  const usInputs = useTaxAnalysisInputsUS();
  const caInputs = useTaxAnalysisInputsCA();

  // Build region-specific inputs as a discriminated union for useTaxAnalysisData hook
  const regionInputsMap: Record<AppRegion, RegionTaxInputs> = {
    [AppRegion.US]: { region: TaxRegionKey.US, values: usInputs.values },
    [AppRegion.CAN]: { region: TaxRegionKey.CA, values: caInputs.values },
  };

  const regionInputs = regionInputsMap[userRegion as AppRegion];

  // Return all setters from both hooks for backward compatibility
  return {
    regionInputs,
    // US setters
    ...usInputs,
    // CA setters
    ...caInputs,
  };
};
